import React, { useContext, useEffect, useState } from 'react';

import './index.scss';
import PriceOptions, {
  getAvlLanguage,
  IPriceOptionFields,
  LanguageOptionProps,
  PriceOptionProps,
} from '../../PriceOptions/PriceOptions';
import { Drawer } from '@mui/material';
import { getAPICall, postAPICall } from '../../../dataFetcher/axiosMethodCalls';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { APIWebstoreContext } from '../../../context/globalContext';
import { addEllipsis, getDateTimePartsFromIso, getCurrencyInUSD } from '../../../utils/helper';
import { Navigate } from 'react-router-dom';
import LoadingIcon from '../../LoadingIcon/LoadingIcon';
import LoadingSection from '../../LoadingSection/LoadingSection';
import nextId from 'react-id-generator';
import { CartAnalyticsInfo, addCartEvent, checkoutEvent } from '../../../utils/analyticsHelper';
import { IFieldSpecificConfig } from '../../../model/IFieldSpecificConfig';
import { ProductType } from '../../../utils/ProductTypes';
import DOMPurify from 'dompurify';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { SessionOptions } from '../../Product/ProductSalesPrice/ProductSalesPrice';

export type CartSliderProps = {
  productId: string;
  productType: string;
  productTitle: string;
  edition: string;
  languageOptions?: Array<LanguageOptionProps>;
  openSlider: boolean;
  closeSlider?: any;
  expandMode?: boolean;
  toggleAccordion: any;
  loadPriceOptions?: boolean;
  isProductActive?: boolean;
  countryConfigFields?: IPriceOptionFields;
  formatOptions?: any;
  term?: string;
  year?: string;
  fieldSpecificConfig?: IFieldSpecificConfig;
  lob?: string;
  selectedSession?: SessionOptions | null;
};

export const CartSlider = (props: CartSliderProps): React.JSX.Element => {
  const [subTotal, setSubTotal] = useState(0);
  const { state, dispatch } = useContext(APIWebstoreContext);
  const [isLoading, setIsLoading] = useState(props.loadPriceOptions);
  const [open, setOpen] = React.useState(true);
  const [languageOptions, setLanguageOptions] = useState(Array<LanguageOptionProps>());
  const [isProductActive, setIsProductActive] = useState(props.isProductActive);
  const [isAddToCart, setIsAddToCart] = useState(false);
  const [isAddToCartAndCheckout, setIsAddToCartAndCheckout] = useState(false);
  const [redirectToCheckout, setRedirectToCheckout] = useState(false);
  const [isItemsUpdated, setIsItemsUpdated] = useState(false);

  const handleLoadingClose = () => {
    setOpen(false);
    setIsLoading(false);
  };

  useEffect(() => {
    if (!props.openSlider) {
      return;
    }

    setIsLoading(props.loadPriceOptions);
    if (props.loadPriceOptions && props.productId?.length > 0) {
      loadPricing(props.productId, props.edition);
    } else if (props.languageOptions) {
      setLanguageOptions(props.languageOptions);
    }
    setIsProductActive(props.isProductActive);
  }, [
    props.productId,
    props.edition,
    props.languageOptions,
    props.isProductActive,
    props.productType,
    props.openSlider,
  ]);

  useEffect(() => {
    if (languageOptions.length === 1 && languageOptions[0].priceOptions.length === 1) {
      const priceOption = languageOptions[0].priceOptions[0];
      if (priceOption.quantity === 0) {
        setLanguageOptions([
          {
            ...languageOptions[0],
            priceOptions: [{ ...priceOption, quantity: 1 }],
          },
        ]);
        setSubTotal(priceOption.unitPrice);
      } else {
        setSubTotal(priceOption.quantity * priceOption.unitPrice);
      }

      setIsItemsUpdated(true);
      return;
    }

    calculateTotalWithOptions(languageOptions);
    checkQuantityCountWithOptions(languageOptions);
  }, [languageOptions]);

  function AddToCart(redirect: boolean) {
    const orderLineItems = languageOptions.flatMap((languageOption) =>
      languageOption.priceOptions
        .filter((priceOption) => priceOption.quantity > 0)
        .map((priceOption) => ({
          productId: props.productId,
          variantId: priceOption.variantId,
          quantity: priceOption.quantity,
          specId: props?.selectedSession?.specId,
          optionId: props?.selectedSession?.optionId,
        }))
    );

    const addToCartRequest = {
      OrderLineItems: orderLineItems,
      LOB: props?.lob,
    };

    postAPICall(
      `${process.env.REACT_APP_CART_URL}/cartOrder/unifiedcreateorder`,
      addToCartRequest
    ).then(function (response: any) {
      if (response) {
        props.closeSlider();
        if (dispatch) {
          dispatch({
            type: 'CART_UPDATE',
            payload: {
              toggleCartQuantityLoad: !state?.toggleCartQuantityLoad,
            },
          });
        }
        setIsAddToCart(false);
        if (redirect) {
          setIsAddToCartAndCheckout(false);
          setRedirectToCheckout(true);
        }
      }
    });
  }

  const handleClose = () => {
    // API call to save cart quanity
    props.closeSlider();
    setSubTotal(0);
  };

  const handleAddToCart = (e: any) => {
    setIsAddToCart(true);
    // API call to save cart quanity
    AddToCart(false);
    formJSONAndPushToGTM();
    e.preventDefault();
  };

  const handleAddToCartAndCheckout = (e: any) => {
    e.preventDefault();
    formJSONAndPushToGTM();

    if (isItemsUpdated) {
      setIsAddToCartAndCheckout(true);
      AddToCart(true);
    } else {
      setRedirectToCheckout(true);
    }
  };

  const calculateTotal = () => {
    calculateTotalWithOptions(languageOptions);
  };

  const calculateTotalWithOptions = (langOptions: Array<LanguageOptionProps>) => {
    if (langOptions) {
      setSubTotal(
        langOptions.reduce((accumulator, options) => {
          return options.priceOptions.reduce(
            (accumulator, price) => (accumulator += price.quantity * price.unitPrice),
            accumulator
          );
        }, 0)
      );
    }
  };

  const loadPricing = (productId: String, edition: String) => {
    if (props.loadPriceOptions && productId && productId.length > 0) {
      let apiUrl = `${process.env.REACT_APP_CATALOG_URL}/catalog/price/${productId}/${props.productType}/`;
      if (props.productType === ProductType.StatisticsReport) {
        apiUrl += `${props.year}/${props.term}`;
      } else if (props.productType !== ProductType.TrainingCourses) {
        let previousEdition = edition && edition.length > 0 ? `${edition}` : '';
        apiUrl = `${apiUrl}${previousEdition}`;
      }

      getAPICall(`${apiUrl}`)
        .then(function (response: any) {
          if (response && response.data) {
            setIsProductActive(response.data.isProductActive);
            calculateTotalWithOptions(response.data.languageOptions);
            checkQuantityCountWithOptions(response.data.languageOptions);

            if (response.data.languageOptions) {
              setLanguageOptions([...response.data.languageOptions]);
            } else {
              const priceOptions: PriceOptionProps[] = [
                {
                  unitPrice: response.data.unitPrice,
                  quantity: response.data.quantity,
                  isActive: response.data.isProductActive,
                },
              ];
              setLanguageOptions([
                {
                  productType: response.data.type,
                  priceOptions: priceOptions,
                  isDefault: true,
                },
              ]);
            }
          }
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  function checkQuantityCount() {
    checkQuantityCountWithOptions(languageOptions);
  }

  function checkQuantityCountWithOptions(langOptions: Array<LanguageOptionProps>) {
    let isItemsAdded = false;

    if (
      langOptions &&
      langOptions.some((options) => options.priceOptions.some((price) => price.quantity > 0))
    ) {
      isItemsAdded = true;
    } else {
      isItemsAdded = false;
    }
    setIsItemsUpdated(isItemsAdded);
  }

  const formJSONAndPushToGTM = () => {
    let value: any;
    value = subTotal.toFixed(2);
    const itemAnalyticsInfo: CartAnalyticsInfo = {
      currency: 'USD',
      value: value,
      items: [],
    };
    if (languageOptions) {
      languageOptions.filter(function (langOptions) {
        langOptions?.priceOptions?.filter(function (priceOptions) {
          let price: any = priceOptions?.unitPrice;
          price = price.toFixed(2);
          if (priceOptions.quantity > 0) {
            itemAnalyticsInfo.items.push({
              item_id: `${props?.productId}`,
              item_name: `${props?.productTitle}`,
              item_brand: `${props?.productType}`,
              item_variant: priceOptions?.variantId ?? '',
              price: price,
              quantity: priceOptions?.quantity,
            });
          }
        });
      });
    }

    addCartEvent(itemAnalyticsInfo);
  };

  function disableCheckoutButton() {
    if (state?.cartCounts?.totalCount === 0 && !isItemsUpdated) {
      return true;
    }
    return false;
  }

  function showGoToCheckoutButton() {
    if (disableCheckoutButton() || (state?.cartCounts?.totalCount !== 0 && !isItemsUpdated)) {
      return true;
    } else {
      return false;
    }
  }

  return (
    <div className="cartSlider">
      <React.Fragment>
        <Drawer anchor="right" open={props.openSlider} onClose={handleClose} className="cartDrawer">
          <IconButton
            onClick={handleClose}
            className="cartSliderClose"
            sx={{ width: 'fit-content' }}
          >
            <CloseIcon />
          </IconButton>
          <h1>Add to cart</h1>
          <hr className="cartSliderHR" />
          {isLoading ? (
            <div className="cartLoadingSection">
              <LoadingSection
                open={open}
                onClick={handleLoadingClose}
                size="fullSize"
                message="Please wait..."
              />
            </div>
          ) : (
            <>
              <div className="salesprice">
                <div className="salesPriceSection">
                  <h1 className="salesPriceProductTitle" key={nextId()}>
                    {props.productTitle}
                    {addEllipsis(props.productTitle)}
                  </h1>
                  {props.selectedSession?.description && (
                    <>
                      <Accordion square className="session-description-accordion">
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          aria-controls="session-description-content"
                          id="session-description-header"
                        >
                          <Typography style={{ marginLeft: 'auto', textDecoration: 'none' }}>
                            Details
                          </Typography>
                        </AccordionSummary>

                        <AccordionDetails style={{ display: 'block' }}>
                          {props.selectedSession && (
                            <>
                              <div className="ils-meta">
                                <span className="label">Starts:</span>{' '}
                                {getDateTimePartsFromIso(props.selectedSession.startDate)?.date}{' '}
                                {props.selectedSession.timezone}
                              </div>
                              <div className="ils-meta">
                                <span className="label">Ends:</span>{' '}
                                {getDateTimePartsFromIso(props.selectedSession.endDate)?.date}{' '}
                                {props.selectedSession.timezone}
                              </div>
                            </>
                          )}
                          <div
                            className="ils-description"
                            dangerouslySetInnerHTML={{
                              __html: DOMPurify.sanitize(props.selectedSession?.description || ''),
                            }}
                          />
                          <hr className="accordion-divider" />
                          <br />
                        </AccordionDetails>
                      </Accordion>
                    </>
                  )}

                  {languageOptions && languageOptions.length > 0 && (
                    <PriceOptions
                      calculateTotal={calculateTotal}
                      checkQuantityCount={checkQuantityCount}
                      key={nextId()}
                      priceOptions={
                        languageOptions.filter((x) => x.isDefault === true)[0]?.priceOptions
                      }
                      isProductActive={isProductActive}
                      formatOptions={props.formatOptions}
                      countryConfigFields={props.countryConfigFields}
                      productType={props.productType}
                      fieldConfig={props.fieldSpecificConfig}
                    />
                  )}
                  {languageOptions &&
                    languageOptions.length > 0 &&
                    getAvlLanguage(
                      languageOptions.filter((x) => x.isDefault === false),
                      calculateTotal,
                      checkQuantityCount,
                      props.productType,
                      props.expandMode,
                      props.toggleAccordion,
                      isProductActive,
                      props.countryConfigFields,
                      props.formatOptions
                    )}
                </div>
              </div>
              <div className="cartNavigationLinkSection">
                <p>
                  <span>Cart Subtotal</span>
                  <span className="cartSubTotal">
                    {subTotal ? getCurrencyInUSD(subTotal) : getCurrencyInUSD(0)}
                  </span>
                </p>
                <div className="cartNavButtons">
                  <a
                    className={`addtoCartButton ${
                      isItemsUpdated ? '' : 'add-to-cart-btn-disable'
                    } `}
                    href="#"
                    {...(!isAddToCart ? { onClick: handleAddToCart } : {})}
                  >
                    {isAddToCart ? (
                      <LoadingIcon />
                    ) : (
                      state?.sitecoreDictionaryData?.ADD_TO_CART || 'ADD TO CART'
                    )}
                  </a>
                  <a
                    className={
                      !disableCheckoutButton()
                        ? 'addtoCartButton addtoCartAndCheckoutButton'
                        : 'addtoCartButton addtoCartAndCheckoutButton add-to-cart-btn-disable'
                    }
                    href="#"
                    {...(!isAddToCartAndCheckout ? { onClick: handleAddToCartAndCheckout } : {})}
                  >
                    {isAddToCartAndCheckout ? (
                      <LoadingIcon />
                    ) : showGoToCheckoutButton() ? (
                      state?.sitecoreDictionaryData?.GO_TO_CHECKOUT || 'GO TO CHECKOUT'
                    ) : (
                      state?.sitecoreDictionaryData?.ADD_TO_CART_AND_CHECKOUT ||
                      'ADD TO CART AND CHECKOUT'
                    )}
                    {redirectToCheckout ? <Navigate to="/orders/checkout" /> : <></>}
                  </a>
                </div>
              </div>
            </>
          )}
        </Drawer>
      </React.Fragment>
    </div>
  );
};

export default CartSlider;
